import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import { animated, useSpring } from 'react-spring'
import Icon, { IconName } from 'components/Icon'
import Spinner from 'components/Spinner'
import { ProPayoutsViewModel } from 'payouts/pro/ProPayoutsViewModel'
import styles from './PayoutSummary.sass'
import { formatMoney } from 'utils/money-util'

interface PayoutSummaryProps {
  vm: ProPayoutsViewModel
}

/**
 * The PayoutSummary
 */
export default observer(function PayoutSummary({ vm }: PayoutSummaryProps) {
  const loading = vm.fetchSummary.pending
  return (
    <div className={cx(styles.grid)}>
      <Stat
        label="Year-to-date earnings"
        value={vm.summary.earningsYearToDate}
        icon="total-paid"
        variant="info"
        loading={loading}
      />
      <Stat
        label="Pending"
        value={vm.summary.pendingEarnings}
        icon="collect-payment"
        variant="info"
        loading={loading}
      />
      <Stat
        label="Lifetime earnings"
        value={vm.summary.lifetimeEarnings}
        icon="chart-arrow-up"
        variant="success"
        loading={loading}
      />
    </div>
  )
})

interface StatProps {
  label: React.ReactNode
  value: number
  icon: IconName
  loading: boolean
  variant: 'info' | 'success'
}

/**
 * Stat
 */
export function Stat({
  label,
  value: valueInput,
  icon,
  variant,
  loading,
}: StatProps) {
  const { value } = useSpring<{ value: number }>({
    value: valueInput,
    from: { value: 0 },
  })
  return (
    <div
      className={cx(styles.stat, styles[variant], loading && styles.loading)}
    >
      <div className={styles.statContent}>
        <div className={styles.icon}>
          <Icon name={icon} size={24} />
        </div>
        <div className={styles.label}>{label}</div>
        <animated.div className={styles.value}>
          {value.to((n) => formatMoney(n))}
        </animated.div>
        {loading && (
          <div className={styles.spinner}>
            <Spinner />
          </div>
        )}
      </div>
    </div>
  )
}
